import React from 'react';
import { Card, CardHeader, CardContent, Typography, Box, Avatar, Chip, Button } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { AccountTree as TreeIcon } from '@mui/icons-material';
import type { PersonRelationshipSummary } from '../../../../types';

interface RelationshipsSnapshotProps {
  relationships: PersonRelationshipSummary | null;
  personId: string;
  showMetadata?: boolean;
  metadataFormat?: 'short' | 'long';
}

/**
 * RelationshipsSnapshot - Family members at a glance
 *
 * Shows: Parents, Spouses, Children (up to 5 each)
 */
export const RelationshipsSnapshot: React.FC<RelationshipsSnapshotProps> = ({
  relationships,
  personId,
  showMetadata = false,
  metadataFormat = 'short'
}) => {
  const navigate = useNavigate();

  const handleViewAllRelationships = () => {
    navigate(`/people/${personId}/relationships`);
  };

  const handleViewInTree = () => {
    navigate(`/tree/${personId}`);
  };

  const handlePersonClick = (relatedPersonId: string) => {
    navigate(`/people/${relatedPersonId}`);
  };

  if (!relationships) {
    return (
      <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        <CardHeader title="Family" />
        <CardContent sx={{ flex: 1 }}>
          <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
            Loading family relationships...
          </Typography>
        </CardContent>
      </Card>
    );
  }

  const hasAnyRelationships =
    (relationships.parents && relationships.parents.length > 0) ||
    (relationships.spouses && relationships.spouses.length > 0) ||
    (relationships.children && relationships.children.length > 0);

  return (
    <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <CardHeader
        title="Family"
        action={
          <Typography
            component="a"
            variant="caption"
            sx={{
              cursor: 'pointer',
              color: 'primary.main',
              textDecoration: 'none',
              '&:hover': { textDecoration: 'underline' }
            }}
            onClick={handleViewAllRelationships}
          >
            View All →
          </Typography>
        }
      />

      <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
        {!hasAnyRelationships ? (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', mb: 2 }}>
              No family relationships added yet.
            </Typography>
            <Button
              variant="outlined"
              size="small"
              startIcon={<TreeIcon />}
              onClick={handleViewInTree}
            >
              View Family Tree
            </Button>
          </>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, flex: 1 }}>
            {/* Parents */}
            {relationships.parents && relationships.parents.length > 0 && (
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, mb: 1, display: 'block' }}>
                  Parents
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {relationships.parents.slice(0, 2).map((parent) => (
                    <Chip
                      key={parent.id}
                      avatar={<Avatar>{parent.firstName.charAt(0)}{parent.lastName.charAt(0)}</Avatar>}
                      label={`${parent.firstName} ${parent.lastName}`}
                      onClick={() => handlePersonClick(parent.id)}
                      sx={{ cursor: 'pointer' }}
                    />
                  ))}
                </Box>
              </Box>
            )}

            {/* Spouses */}
            {relationships.spouses && relationships.spouses.length > 0 && (
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, mb: 1, display: 'block' }}>
                  {relationships.spouses.length === 1 ? 'Spouse' : 'Spouses'}
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {relationships.spouses.slice(0, 3).map((spouse) => (
                    <Chip
                      key={spouse.id}
                      avatar={<Avatar>{spouse.firstName.charAt(0)}{spouse.lastName.charAt(0)}</Avatar>}
                      label={`${spouse.firstName} ${spouse.lastName}`}
                      onClick={() => handlePersonClick(spouse.id)}
                      color="primary"
                      sx={{ cursor: 'pointer' }}
                    />
                  ))}
                </Box>
              </Box>
            )}

            {/* Children */}
            {relationships.children && relationships.children.length > 0 && (
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, mb: 1, display: 'block' }}>
                  Children {relationships.children.length > 5 && `(${relationships.children.length})`}
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {relationships.children.slice(0, 5).map((child) => (
                    <Chip
                      key={child.id}
                      avatar={<Avatar>{child.firstName.charAt(0)}{child.lastName.charAt(0)}</Avatar>}
                      label={`${child.firstName} ${child.lastName}`}
                      onClick={() => handlePersonClick(child.id)}
                      variant="outlined"
                      sx={{ cursor: 'pointer' }}
                    />
                  ))}
                  {relationships.children.length > 5 && (
                    <Chip
                      label={`+${relationships.children.length - 5} more`}
                      onClick={handleViewAllRelationships}
                      variant="outlined"
                      sx={{ cursor: 'pointer' }}
                    />
                  )}
                </Box>
              </Box>
            )}

            {/* View in Tree button */}
            <Box sx={{ mt: 'auto', pt: 2 }}>
              <Button
                variant="outlined"
                size="small"
                fullWidth
                startIcon={<TreeIcon />}
                onClick={handleViewInTree}
              >
                View Family Tree
              </Button>
            </Box>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};
